import RequestedFeature from '@carvana/merch-contracts/RequestedFeature';
import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants/srp';
import SrpNativeCapability from '~/constants/srp/nativeCapability';
import AppliedFiltersContext, {
  type PageSource
} from '~/context/common/AppliedFiltersContext';
import { useIsNative } from '~/context/common/DeviceContext';
import { useIsNativeCapabilityActive } from '~/context/common/NativeContext';
import { useIsSebastianLoadedOnNative } from '~/context/common/SebastianContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest
} from '~/context/common/TradeContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import FinanceContext, {
  formatFinanceInfo
} from '~/context/srp/FinanceContext';
import InventoryContext from '~/context/srp/InventoryContext';
import SearchContext from '~/context/srp/SearchContext';
import type { InventoryRequest } from '~/contracts/common';
import useGetLowInventory from '~/hooks/srp/services/useGetLowInventory';
import useGenerateAnalyticsData from '~/hooks/srp/useGenerateAnalyticsData';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';
import type { InventoryResponse } from '~/services/srp/contracts';
import fetchInventory from '~/services/srp/fetchInventory';
import { generateErrorLogger } from '~/utilities/loggers';

function useShouldFetch(args: {
  fetchSearchTrigger: number;
  initialValue: boolean;
  isLoaded: boolean;
  pageSourceRef: React.MutableRefObject<PageSource>;
  request: InventoryRequest;
  searchAnalyzeTrigger: boolean;
}): boolean {
  const {
    fetchSearchTrigger,
    initialValue,
    isLoaded,
    pageSourceRef,
    request,
    searchAnalyzeTrigger
  } = args;

  const [shouldFetch, setShouldFetch] = useState(initialValue);

  // NOTE: The order of these effects is important
  useSubsequentEffect(() => {
    if (isLoaded) setShouldFetch(false);
  }, [isLoaded]);

  useSubsequentEffect(() => {
    // Load More already fetched and appended this page itself; the matching
    // pageNumber sync that follows shouldn't trigger a second, replace-style fetch.
    if (pageSourceRef.current === 'loadMore') return;
    setShouldFetch(true);
  }, [pageSourceRef, request]);

  useSubsequentEffect(() => {
    if (searchAnalyzeTrigger) setShouldFetch(true);
  }, [searchAnalyzeTrigger]);

  useSubsequentEffect(() => {
    if (fetchSearchTrigger) setShouldFetch(true);
  }, [fetchSearchTrigger]);

  return shouldFetch;
}

export default function useGetInventory(
  fetchedInventoryOnServer?: boolean
): void {
  const { pageSourceRef, setAppliedFilters } = useContext(
    AppliedFiltersContext
  );
  const {
    fetchSearchTrigger,
    inventory: { data, setData, setError, setLoading, isLoaded },
    loadMoreResultsTileInfo: { reset: resetLoadMoreResultsTileInfo },
    setPreviousSearchRequestId,
    setSimilarVehicles,
    searchAnalyzeTrigger,
    setSearchAnalyzeTrigger
  } = useContext(InventoryContext);
  const { setFinanceInfo, setIsFinanceInitialized, setShouldRefreshSoftPull } =
    useContext(FinanceContext);
  const {
    tradeInfoFromApis: { setData: setTradeInfo }
  } = useContext(SearchContext);
  const { setTradeInApplied } = useContext(TradeContext);

  const { request, conditionalRequestedFeatures } = useGenerateInventoryRequest(
    MAX_PAGE_SIZE_DEFAULT
  );
  const generateAnalyticsData = useGenerateAnalyticsData();
  const [inventoryResponse, setInventoryResponse] =
    useState<InventoryResponse>();
  const getLowInventory = useGetLowInventory();
  const shouldFetch = useShouldFetch({
    fetchSearchTrigger,
    initialValue: !fetchedInventoryOnServer,
    isLoaded,
    pageSourceRef,
    request,
    searchAnalyzeTrigger
  });

  const isNative = useIsNative();
  const hasSebsTileCapability = useIsNativeCapabilityActive(
    SrpNativeCapability.SebsTile
  );
  const isSebastianLoadedOnNative = useIsSebastianLoadedOnNative();
  const showComposerTreatment = useShowComposerTreatment();
  const isSebastianTileSupported =
    !showComposerTreatment &&
    (isNative ? hasSebsTileCapability && isSebastianLoadedOnNative : true);

  // Need value when fetching inventory not when generating the request
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const preferredAcquisitionNameRef = useRef(preferredAcquisitionName);

  useEffect(() => {
    preferredAcquisitionNameRef.current = preferredAcquisitionName;
  }, [preferredAcquisitionName]);

  const conditionalRequestedFeaturesRef = useRef<RequestedFeature[]>([]);
  useEffect(() => {
    conditionalRequestedFeaturesRef.current = conditionalRequestedFeatures;
  }, [conditionalRequestedFeatures]);

  const previousSearchRequestIdRef = useRef(data.searchRequestId);
  useEffect(() => {
    previousSearchRequestIdRef.current = data.searchRequestId;
  }, [data.searchRequestId]);

  const requestForLowInventoryRef = useRef<InventoryRequest>(undefined);
  useEffect(() => {
    requestForLowInventoryRef.current = {
      ...request,
      analyticsData: generateAnalyticsData()
    };
  }, [generateAnalyticsData, request]);

  useEffectWithController(
    controller => {
      if (!shouldFetch) return;

      // Reset the searchAnalyzeTrigger flag when starting a new search
      if (searchAnalyzeTrigger) {
        setSearchAnalyzeTrigger(false);
      }

      setLoading();

      setSimilarVehicles({
        searchRequestId: '',
        vehicles: []
      });

      fetchInventory(
        {
          ...request,
          analyticsData: generateAnalyticsData(),
          requestedFeatures: request.requestedFeatures.concat(
            conditionalRequestedFeaturesRef.current
          ),
          preferredAcquisitionName: preferredAcquisitionNameRef.current
        },
        controller,
        {
          'x-cvna-ncc-disabled': String(isNative),
          'x-cvna-sebs-srp': String(isSebastianTileSupported)
        }
      )
        .then(res => {
          const {
            effectiveFilters,
            financeInfo,
            inventory,
            searchRequestId,
            tradeInfo,
            userDeliveryInfo
          } = res.data;
          const formattedFinanceInfo =
            financeInfo && !financeInfo.pricingHasErrored
              ? formatFinanceInfo(financeInfo)
              : undefined;
          setPreviousSearchRequestId(previousSearchRequestIdRef.current);
          setData({
            inventory,
            searchRequestId,
            userDeliveryInfo
          });

          resetLoadMoreResultsTileInfo();
          pageSourceRef.current = 'search';

          if (
            request.requestedFeatures.includes(RequestedFeature.LoanTermPricing)
          ) {
            setFinanceInfo(formattedFinanceInfo);
            setIsFinanceInitialized(true);
            setShouldRefreshSoftPull(false);
          }
          setInventoryResponse(res.data);

          if (tradeInfo) {
            setTradeInfo(tradeInfo);
            const isActive =
              tradeInfo.status === 'active' &&
              tradeInfo.totalTradeInEquity !== 0 &&
              tradeInfo.totalTradeInValue !== 0;

            if (isActive && tradeInfo.applyTradeIn != null) {
              setTradeInApplied(tradeInfo.applyTradeIn);
            }
          }

          if (effectiveFilters) {
            setAppliedFilters(effectiveFilters);
          }
        })
        .catch(
          generateErrorLogger(
            'Inventory',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      generateAnalyticsData,
      isNative,
      isSebastianTileSupported,
      pageSourceRef,
      request,
      resetLoadMoreResultsTileInfo,
      searchAnalyzeTrigger,
      setAppliedFilters,
      setData,
      setError,
      setFinanceInfo,
      setIsFinanceInitialized,
      setLoading,
      setPreviousSearchRequestId,
      setSearchAnalyzeTrigger,
      setShouldRefreshSoftPull,
      setSimilarVehicles,
      setTradeInApplied,
      setTradeInfo,
      shouldFetch
    ]
  );

  useEffectWithController(
    controller => {
      if (inventoryResponse && requestForLowInventoryRef.current) {
        void getLowInventory(
          requestForLowInventoryRef.current,
          inventoryResponse,
          controller
        );
      }
    },
    [getLowInventory, inventoryResponse]
  );
}
